import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Library as LibIcon, Star, Copy, Pencil, Trash2, CalendarPlus, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { PageHeader, EmptyState, Spinner } from "../components/common";
import { FormDialog } from "../components/FormDialog";
import { useResource } from "../hooks/useResource";
import { api, formatErr, CHANNELS, channelLabel, copy, fmtDate } from "../lib/api";

const CH_OPTS = CHANNELS.map((c) => ({ value: c.id, label: c.label }));
const FIELDS = [
  { name: "title", label: "Título", required: true },
  { name: "channel", label: "Canal", type: "select", options: CH_OPTS, half: true },
  { name: "kind", label: "Formato", half: true },
  { name: "body", label: "Conteúdo", type: "textarea", rows: 10, required: true },
  { name: "favorite", label: "Favorito", type: "checkbox" },
];
const SCHED = [{ name: "date", label: "Data", type: "date", required: true, half: true }, { name: "time", label: "Horário", type: "time", half: true }];

export default function Library() {
  const r = useResource("contents");
  const [edit, setEdit] = useState({ open: false, item: null });
  const [sched, setSched] = useState({ open: false, item: null });
  const [ch, setCh] = useState("");
  const [q, setQ] = useState("");
  const [fav, setFav] = useState(false);
  const list = useMemo(() => r.items.filter((c) => (!ch || c.channel === ch) && (!fav || c.favorite) && (c.title + c.body).toLowerCase().includes(q.toLowerCase())), [r.items, ch, q, fav]);

  const toggleFav = (c) => r.save({ ...c, favorite: !c.favorite }, c.id);
  const schedule = async (b) => {
    const c = sched.item;
    try { await api.post("/events", { title: c.title, date: b.date, time: b.time, channel: c.channel, content_id: c.id }); toast.success("Agendado no calendário!"); return true; }
    catch (e) { toast.error(formatErr(e)); return false; }
  };

  return (
    <div>
      <PageHeader eyebrow="Biblioteca" title="Conteúdos salvos" desc="Todos os textos e roteiros gerados, organizados por canal." />
      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-3 text-zinc-500" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="pl-9 bg-zinc-900 border-zinc-800" data-testid="library-search-input" /></div>
        <select className="field sm:w-44" value={ch} onChange={(e) => setCh(e.target.value)} data-testid="library-channel-filter"><option value="">Todos os canais</option>{CH_OPTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select>
        <button onClick={() => setFav(!fav)} className={`h-10 px-4 rounded-md border text-sm flex items-center gap-2 ${fav ? "border-amber-500/50 text-amber-300 bg-amber-500/10" : "border-zinc-800 text-zinc-400"}`} data-testid="library-fav-filter"><Star className="h-4 w-4" />Favoritos</button>
      </div>
      {r.loading ? <Spinner /> : list.length === 0 ? (
        <EmptyState icon={LibIcon} title="Biblioteca vazia" desc="Gere conteúdos com a IA e salve aqui." action={<Link to="/gerador" className="btn-gold rounded-full px-5 py-2 text-sm">Abrir gerador</Link>} />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="card-lux p-4 flex flex-col rise" data-testid={`content-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><div className="font-semibold text-sm truncate">{c.title}</div><div className="text-[11px] text-zinc-500 mt-0.5">{channelLabel(c.channel)} • {c.kind} • {fmtDate(c.created_at)}</div></div>
                <button onClick={() => toggleFav(c)} data-testid={`content-fav-${c.id}`}><Star className={`h-4 w-4 ${c.favorite ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} /></button>
              </div>
              <p className="text-xs text-zinc-300 whitespace-pre-line mt-3 line-clamp-6 flex-1">{c.body}</p>
              <div className="flex justify-end gap-1 mt-3 border-t border-zinc-800 pt-2">
                <button className="p-2 text-zinc-500 hover:text-white" onClick={() => copy(c.body).then(() => toast.success("Copiado!"))} data-testid={`content-copy-${c.id}`}><Copy className="h-4 w-4" /></button>
                <button className="p-2 text-zinc-500 hover:text-amber-400" onClick={() => setSched({ open: true, item: c })} data-testid={`content-schedule-${c.id}`}><CalendarPlus className="h-4 w-4" /></button>
                <button className="p-2 text-zinc-500 hover:text-amber-400" onClick={() => setEdit({ open: true, item: c })} data-testid={`content-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                <button className="p-2 text-zinc-500 hover:text-rose-400" onClick={() => r.remove(c.id)} data-testid={`content-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <FormDialog open={edit.open} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} title="Editar conteúdo" fields={FIELDS} initial={edit.item} testid="content-form"
        onSubmit={(b) => r.save({ ...edit.item, ...b }, edit.item.id)} />
      <FormDialog open={sched.open} onOpenChange={(o) => setSched((s) => ({ ...s, open: o }))} title="Agendar no calendário" fields={SCHED} initial={{}} testid="schedule-form" submitLabel="Agendar" onSubmit={schedule} />
    </div>
  );
}
